/**
 * analysisEngine.js — v2
 * Opción B+C: Union of OR-blocks per skill, then intersect across skills.
 * Adds PARTIAL_OVERLAP category for partial compatibility.
 */

import { classifyVariable, isEnumVariable, getEnumLabel } from './variableClassifier';

// ── Result categories ──────────────────────────────────────────────────────────
export const RESULT_CATEGORY = {
    INCOMPATIBLE_RACE: 'INCOMPATIBLE_RACE',    // 🔴 No overlap at race level
    INCOMPATIBLE_MOMENT: 'INCOMPATIBLE_MOMENT',  // 🟡 No overlap at moment level
    PARTIAL_OVERLAP: 'PARTIAL_OVERLAP',       // 🟠 Some OR paths overlap
    COMPATIBLE: 'COMPATIBLE',            // 🟢 Full or equivalent overlap
    SUBSET: 'SUBSET',                // 🔵 One is more restrictive
    NO_CONFLICT: 'NO_CONFLICT',           // ⚪ Only one skill has this var
};

// ── Domain computation per single AND-block ────────────────────────────────────

/**
 * Compute domain from an AND-block's [op, val] pairs for a given variable.
 * Returns: { type: 'set'|'range', allowed: Set|null, min, max, isEmpty }
 */
const computeAndBlockDomain = (variable, opValuePairs) => {
    if (!opValuePairs || opValuePairs.length === 0) {
        return { type: 'any', isEmpty: false };
    }

    if (isEnumVariable(variable)) {
        // Build the allowed set from == / != operators
        let allowed = null;
        for (const [op, rawVal] of opValuePairs) {
            const val = Number(rawVal);
            let blockSet = new Set();
            if (op === '==') {
                blockSet.add(val);
            } else if (op === '!=') {
                blockSet.add(`NOT_${val}`);
            } else if (op === '>=' || op === '>') {
                // Treat as "any" for enum (can't enumerate without full domain)
                blockSet = null;
            }

            if (blockSet === null) continue; // 'any' — skip
            if (allowed === null) {
                allowed = blockSet;
            } else {
                allowed = new Set([...allowed].filter(v => blockSet.has(v)));
            }
        }
        return {
            type: 'set',
            allowed,
            isEmpty: allowed !== null && allowed.size === 0,
        };
    } else {
        // Numeric interval
        let min = -Infinity;
        let max = Infinity;
        for (const [op, rawVal] of opValuePairs) {
            const v = Number(rawVal);
            if (op === '>=') min = Math.max(min, v);
            else if (op === '>') min = Math.max(min, v + 0.001);
            else if (op === '<=') max = Math.min(max, v);
            else if (op === '<') max = Math.min(max, v - 0.001);
            else if (op === '==') { min = Math.max(min, v); max = Math.min(max, v); }
        }
        return {
            type: 'range',
            min: min === -Infinity ? null : min,
            max: max === Infinity ? null : max,
            isEmpty: min > max,
        };
    }
};

/** Union two domains of the same type */
const unionDomains = (d1, d2) => {
    if (!d1 || d1.type === 'any') return d2;
    if (!d2 || d2.type === 'any') return d1;
    if (d1.type === 'set' && d2.type === 'set') {
        const union = new Set([...(d1.allowed || []), ...(d2.allowed || [])]);
        return { type: 'set', allowed: union, isEmpty: false };
    }
    if (d1.type === 'range' && d2.type === 'range') {
        return {
            type: 'range',
            min: d1.isEmpty ? d2.min : (d2.isEmpty ? d1.min : Math.min(d1.min ?? -Infinity, d2.min ?? -Infinity) === -Infinity ? null : Math.min(d1.min ?? Infinity, d2.min ?? Infinity)),
            max: d1.isEmpty ? d2.max : (d2.isEmpty ? d1.max : Math.max(d1.max ?? Infinity, d2.max ?? Infinity) === Infinity ? null : Math.max(d1.max ?? -Infinity, d2.max ?? -Infinity)),
            isEmpty: false,
        };
    }
    return d1; // fallback
};

/** Intersect two domains */
const intersectDomains = (d1, d2) => {
    if (!d1 || d1.type === 'any') return d2;
    if (!d2 || d2.type === 'any') return d1;
    if (d1.type === 'set' && d2.type === 'set') {
        const inter = new Set([...(d1.allowed || [])].filter(v => (d2.allowed || new Set()).has(v)));
        return { type: 'set', allowed: inter, isEmpty: inter.size === 0 };
    }
    if (d1.type === 'range' && d2.type === 'range') {
        const min = Math.max(d1.min ?? -Infinity, d2.min ?? -Infinity);
        const max = Math.min(d1.max ?? Infinity, d2.max ?? Infinity);
        return {
            type: 'range',
            min: min === -Infinity ? null : min,
            max: max === Infinity ? null : max,
            isEmpty: min > max,
        };
    }
    return d1; // fallback
};

// ── Per-skill variable extraction with OR-block info ───────────────────────────

/**
 * For a given scenario, extract per-variable data grouped by OR-block.
 * Returns: { [variable]: { condType, orBlocks: [{blockIdx, opValues: [[op,val]]}], unionDomain } }
 */
const extractSkillVariableData = (scenarioEffects, skillName) => {
    const result = {}; // variable → { condType, orBlocks, unionDomain }

    const processCondArray = (condArray, condType) => {
        if (!Array.isArray(condArray)) return;

        condArray.forEach((blockObj, blockIdx) => {
            if (!blockObj || typeof blockObj !== 'object') return;

            Object.entries(blockObj).forEach(([variable, opValuePairs]) => {
                if (!Array.isArray(opValuePairs)) return;

                // Normalize: opValuePairs is [["==", 1]] or [[["=="],[1]]] etc.
                const pairs = opValuePairs.map(p => Array.isArray(p) ? p : [p]);

                if (!result[variable]) {
                    result[variable] = { condType, skillName, orBlocks: [], unionDomain: null };
                }

                result[variable].orBlocks.push({ blockIdx, opValues: pairs });

                // Compute this block's domain and union into existing
                const blockDomain = computeAndBlockDomain(variable, pairs);
                result[variable].unionDomain = result[variable].unionDomain
                    ? unionDomains(result[variable].unionDomain, blockDomain)
                    : blockDomain;
            });
        });
    };

    processCondArray(scenarioEffects?.Conditions, 'Condition');
    processCondArray(scenarioEffects?.Preconditions, 'Precondition');

    return result;
};

// ── Categorization ─────────────────────────────────────────────────────────────

const categorize = (intersection, perSkillDomains, variableType) => {
    if (perSkillDomains.length < 2) return RESULT_CATEGORY.NO_CONFLICT;

    if (intersection.isEmpty) {
        return variableType === 'race'
            ? RESULT_CATEGORY.INCOMPATIBLE_RACE
            : RESULT_CATEGORY.INCOMPATIBLE_MOMENT;
    }

    // Check if intersection == union of all (fully compatible)
    // Simple heuristic: if all skills have the same allowed set, it's COMPATIBLE
    if (intersection.type === 'set' && intersection.allowed) {
        const allSizes = perSkillDomains.map(d => d?.allowed?.size ?? Infinity);
        const minSize = Math.min(...allSizes);
        if (intersection.allowed.size === minSize) return RESULT_CATEGORY.COMPATIBLE;
        return RESULT_CATEGORY.PARTIAL_OVERLAP;
    }
    if (intersection.type === 'range') {
        return RESULT_CATEGORY.COMPATIBLE;
    }
    return RESULT_CATEGORY.COMPATIBLE;
};

// ── Format helpers ─────────────────────────────────────────────────────────────

const formatEnumValues = (variable, domain) => {
    if (!domain || domain.type !== 'set' || !domain.allowed) return '?';
    return [...domain.allowed]
        .filter(v => !String(v).startsWith('NOT_'))
        .map(v => getEnumLabel(variable, v))
        .join(', ') || '—';
};

const formatDomainSummary = (variable, intersection) => {
    if (!intersection || intersection.isEmpty) return 'No shared conditions — incompatible.';
    if (intersection.type === 'set') return `Shared: ${formatEnumValues(variable, intersection)}`;
    if (intersection.type === 'range') {
        const { min, max } = intersection;
        if (min !== null && max !== null) return `Shared range: ${min} – ${max}`;
        if (min !== null) return `Shared: ≥ ${min}`;
        if (max !== null) return `Shared: ≤ ${max}`;
        return 'No numeric constraint (unconstrained)';
    }
    return '';
};

// ── Main export ────────────────────────────────────────────────────────────────

/**
 * analyzeSkills(selectedSkills)
 * @param {Array} selectedSkills — [{ Id_skill, skillName, skill_effects, chosenScenario }]
 * @returns { raceVariables, momentVariables, summary }
 */
export const analyzeSkills = (selectedSkills) => {
    if (!selectedSkills || selectedSkills.length < 2) {
        return {
            raceVariables: [], momentVariables: [],
            summary: { hasRaceConflict: false, hasMomentConflict: false, totalAnalyzed: 0 },
        };
    }

    // 1. Extract per-skill variable data for chosen scenario
    const allSkillData = selectedSkills.map(skill => {
        const scenario = skill.skill_effects?.find(e => e.group_id === skill.chosenScenario)
            || skill.skill_effects?.[0];
        return {
            skillName: skill.skillName,
            variableData: extractSkillVariableData(scenario, skill.skillName),
        };
    });

    // 2. Collect all variables across all skills
    const allVariables = new Set();
    allSkillData.forEach(({ variableData }) => Object.keys(variableData).forEach(v => allVariables.add(v)));

    // 3. For each variable, intersect union-domains across all skills that have it
    const raceVariables = [];
    const momentVariables = [];

    for (const variable of allVariables) {
        const skillsWithVar = allSkillData.filter(s => s.variableData[variable]);
        if (skillsWithVar.length === 0) continue;

        const perSkillDomains = skillsWithVar.map(s => s.variableData[variable].unionDomain);

        // Intersect all skill domains
        let intersection = perSkillDomains[0];
        for (let i = 1; i < perSkillDomains.length; i++) {
            intersection = intersectDomains(intersection, perSkillDomains[i]);
        }

        const variableType = classifyVariable(variable);
        const category = categorize(intersection, perSkillDomains, variableType);
        const summary = formatDomainSummary(variable, intersection);

        // For UI: build per-skill entry list with OR-block info
        const entries = allSkillData.flatMap(({ skillName, variableData }) => {
            const vd = variableData[variable];
            if (!vd) return [];
            return vd.orBlocks.map(({ blockIdx, opValues }) => ({
                variable,
                operator: opValues[0]?.[0] ?? '?',
                value: opValues[0]?.[1] ?? '?',
                allPairs: opValues, // full AND pairs for this block
                blockIdx,
                totalBlocks: vd.orBlocks.length,
                condType: vd.condType,
                skillName,
            }));
        });

        const result = {
            variable, variableType, entries,
            perSkillDomains: skillsWithVar.map((s, i) => ({
                skillName: s.skillName,
                domain: perSkillDomains[i],
            })),
            intersection,
            category,
            summary,
        };

        if (variableType === 'race') raceVariables.push(result);
        else momentVariables.push(result);
    }

    // 4. Sort — worst first
    const sortPriority = (r) => {
        if (r.category === RESULT_CATEGORY.INCOMPATIBLE_RACE) return 0;
        if (r.category === RESULT_CATEGORY.INCOMPATIBLE_MOMENT) return 1;
        if (r.category === RESULT_CATEGORY.PARTIAL_OVERLAP) return 2;
        if (r.category === RESULT_CATEGORY.SUBSET) return 3;
        if (r.category === RESULT_CATEGORY.COMPATIBLE) return 4;
        return 5;
    };
    raceVariables.sort((a, b) => sortPriority(a) - sortPriority(b));
    momentVariables.sort((a, b) => sortPriority(a) - sortPriority(b));

    // 5. Summary
    const hasRaceConflict = raceVariables.some(v => v.category === RESULT_CATEGORY.INCOMPATIBLE_RACE);
    const hasMomentConflict = momentVariables.some(v => v.category === RESULT_CATEGORY.INCOMPATIBLE_MOMENT);
    const hasPartialOverlap = [...raceVariables, ...momentVariables].some(v => v.category === RESULT_CATEGORY.PARTIAL_OVERLAP);

    return {
        raceVariables,
        momentVariables,
        summary: {
            hasRaceConflict,
            hasMomentConflict,
            hasPartialOverlap,
            totalAnalyzed: raceVariables.length + momentVariables.length,
            raceConflictCount: raceVariables.filter(v => v.category === RESULT_CATEGORY.INCOMPATIBLE_RACE).length,
            momentConflictCount: momentVariables.filter(v => v.category === RESULT_CATEGORY.INCOMPATIBLE_MOMENT).length,
            partialCount: [...raceVariables, ...momentVariables].filter(v => v.category === RESULT_CATEGORY.PARTIAL_OVERLAP).length,
        },
    };
};
